import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Box,
  Divider,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Alert,
} from "@mui/material";
import { getCookie, getHeaders } from "../../utils/api";
import envConfig from "../../config";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import BackdropLoading from "../../components/BackdropLoading/BackdropLoading";
import LogoutEverywhereButton from "../../components/LogoutEverywhereButton/LogoutEverywhereButton";
import { formatPhoneDisplay, normalizePhoneForApi } from "../../utils/formatPhone";
import { sendForgotPasswordEmail } from "../../utils/sendForgotPasswordEmail";
import { useErrorModal } from "../../context/ErrorModalContext";
import { completeAccountDeletion } from "./accountDeletion";

const showApiError = (data, showError, enqueueSnackbar) => {
  if (data?.ErMsg && typeof data.ErMsg === "object") {
    showError(data.ErMsg);
    return;
  }
  enqueueSnackbar(
    data?.ErrorMessage || data?.ErMsg?.Text || "An error occurred. Please try again.",
    { variant: "error" }
  );
};

const GlobalUserEdit = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showError } = useErrorModal();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [userData, setUserData] = useState(null);
  const [formState, setFormState] = useState({
    firstName: "",
    lastName: "",
    nickName: "",
    phone: "",
    email: "",
  });
  const [originalPhone, setOriginalPhone] = useState("");
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [phoneChangeDialogOpen, setPhoneChangeDialogOpen] = useState(false);
  const [passwordSentDialogOpen, setPasswordSentDialogOpen] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const headers = getHeaders();

  const applyUserToForm = (user) => {
    const phone = user.Phone || "";
    setUserData(user);
    setFormState({
      firstName: user.FirstName || "",
      lastName: user.LastName || "",
      nickName: user.NickName || "",
      phone,
      email: user.Email || "",
    });
    setOriginalPhone(normalizePhoneForApi(phone));
    setIsChanged(false);
  };

  const fetchUserData = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const response = await fetch(
        `https://${envConfig.apiD2}/api/global/get-user-details`,
        { headers }
      );
      const data = await response.json();
      if (data.SuccessYN === "Y" && data.User) {
        applyUserToForm(data.User);
      } else {
        setFetchError(true);
        showApiError(data, showError, enqueueSnackbar);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
      setFetchError(true);
      enqueueSnackbar("Unable to load your details. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormState((prevState) => ({
      ...prevState,
      [field]: event.target.value,
    }));
    setIsChanged(true);
  };

  const namesChanged =
    formState.firstName !== (userData?.FirstName || "") ||
    formState.lastName !== (userData?.LastName || "") ||
    formState.nickName !== (userData?.NickName || "");

  const phoneChanged =
    normalizePhoneForApi(formState.phone) !== originalPhone;

  const handleSave = async () => {
    setLoading(true);
    let savedSomething = false;

    try {
      if (namesChanged) {
        const response = await fetch(
          `https://${envConfig.apiD2}/api/global/update-user-basic-details`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              FirstName: formState.firstName,
              LastName: formState.lastName,
              NickName: formState.nickName,
            }),
          }
        );
        const data = await response.json();

        if (data.SuccessYN === "Y") {
          setUserData((prev) => ({
            ...prev,
            FirstName: formState.firstName,
            LastName: formState.lastName,
            NickName: formState.nickName,
          }));
          savedSomething = true;
        } else {
          showApiError(data, showError, enqueueSnackbar);
          return;
        }
      }

      if (phoneChanged) {
        const normalizedPhone = normalizePhoneForApi(formState.phone);
        const response = await fetch(
          `https://${envConfig.apiD2}/api/confirmations/request-change-of-phone-number?NewPhone=${encodeURIComponent(normalizedPhone)}`,
          { headers }
        );
        const data = await response.json();

        if (data.SuccessYN === "Y") {
          setPhoneChangeDialogOpen(true);
          savedSomething = true;
        } else {
          showApiError(data, showError, enqueueSnackbar);
          return;
        }
      }

      if (savedSomething) {
        setIsChanged(false);
        if (namesChanged) {
          enqueueSnackbar("Your details have been saved.", { variant: "success" });
        }
      }
    } catch (error) {
      console.error("Error saving user data", error);
      enqueueSnackbar("Unable to save your details. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!executeRecaptcha || !userData?.Email) return;

    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha("reset_password");
    } catch {
      enqueueSnackbar("reCAPTCHA verification failed. Please try again.", {
        variant: "error",
      });
      return;
    }

    setChangePasswordLoading(true);
    try {
      const { isEmailSent } = await sendForgotPasswordEmail(
        userData.Email,
        recaptchaToken
      );
      if (isEmailSent) {
        setPasswordSentDialogOpen(true);
      } else {
        enqueueSnackbar(
          "Account associated with this email could not be found.",
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error sending password reset email", error);
      enqueueSnackbar("An error occurred while sending the recovery email.", {
        variant: "error",
      });
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/user-dashboard");
  };

  const handleDeleteMyAccount = async () => {
    setDeleteLoading(true);
    try {
      const xxuId = userData?.XxuID ?? getCookie("globalUserId");
      const url = `https://${
        envConfig.apiDev2
      }/api/front-end/delete-global-user?XxuID=${encodeURIComponent(
        xxuId
      )}&BaseHostURL=${encodeURIComponent(envConfig.loginUrl)}`;
      const response = await fetch(url, {
        method: "POST",
        headers,
      });
      const data = await response.json();
      if (data.SuccessYN === "Y") {
        setDeleteDialogOpen(false);
        completeAccountDeletion();
      }
    } catch (error) {
      console.error("Error deleting global user details", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && !userData) {
    return <BackdropLoading open={true} />;
  }

  if (fetchError && !userData) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" color="error" gutterBottom>
          Unable to load your details.
        </Typography>
        <Button variant="contained" onClick={fetchUserData}>
          Retry
        </Button>
      </Container>
    );
  }

  const showMfaPrompt = userData?.HasMfaYN === "N";

  return (
    <>
      <Container maxWidth="md">
        <Typography variant="h4" gutterBottom>
          Edit User Profile - {userData.FirstName} {userData.LastName}
        </Typography>
        <Box display="flex" justifyContent="flex-end" mb={2} flexWrap="wrap" gap={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!isChanged || loading}
            data-testid="global-user-edit--Button-0">
            {loading ? <CircularProgress size={24} /> : "Save"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancel}
            disabled={loading}
            data-testid="global-user-edit--Button-1">
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleChangePassword}
            disabled={changePasswordLoading || loading}
            data-testid="global-user-edit--change-password-button">
            {changePasswordLoading ? (
              <CircularProgress size={24} />
            ) : (
              "Change Password"
            )}
          </Button>
          <Button
            onClick={() => navigate("/mfa-setup")}
            variant="contained"
            color="primary"
            data-testid="global-user-edit--Button-2">
            User 2FA
          </Button>
          <LogoutEverywhereButton variant="contained" size="medium" />
        </Box>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Personal Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {showMfaPrompt && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Set up your Multifactor Authentication app:{" "}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate("/mfa-setup")}
                sx={{ p: 0, minWidth: 0, verticalAlign: "baseline", textTransform: "none" }}
                data-testid="global-user-edit--mfa-setup-link">
                Set up now
              </Button>
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={formState.email}
                InputProps={{ readOnly: true }}
                data-testid="global-user-edit--TextField-email"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formState.firstName}
                onChange={handleChange("firstName")}
                data-testid="global-user-edit--TextField-0"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formState.lastName}
                onChange={handleChange("lastName")}
                data-testid="global-user-edit--TextField-1"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nick Name"
                value={formState.nickName}
                onChange={handleChange("nickName")}
                data-testid="global-user-edit--TextField-2"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Mobile Phone Number"
                value={formatPhoneDisplay(formState.phone, false)}
                onChange={(e) => {
                  handleChange("phone")({ target: { value: e.target.value } });
                }}
                data-testid="global-user-edit--TextField-3"
              />
            </Grid>
          </Grid>
        </Paper>
        <Box mt={3}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={loading || deleteLoading}
            data-testid="global-user-edit--delete-details-button">
            Delete my account
          </Button>
        </Box>
      </Container>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        data-testid="global-user-edit--delete-details-dialog">
        <DialogContent data-testid="global-user-edit--delete-details-dialog-content">
          <Typography>
            Your personal details stored to log you in will be removed.
            Continue?
          </Typography>
        </DialogContent>
        <DialogActions data-testid="global-user-edit--delete-details-dialog-actions">
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteLoading}
            data-testid="global-user-edit--delete-details-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteMyAccount}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            data-testid="global-user-edit--delete-details-confirm">
            {deleteLoading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={phoneChangeDialogOpen}
        onClose={() => setPhoneChangeDialogOpen(false)}
        data-testid="global-user-edit--phone-change-dialog">
        <DialogContent>
          <Typography>
            Change to the phone number was requested. SMS was sent to your new
            number, with a confirmation link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPhoneChangeDialogOpen(false)}
            variant="contained"
            data-testid="global-user-edit--phone-change-ok">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={passwordSentDialogOpen}
        onClose={() => setPasswordSentDialogOpen(false)}
        data-testid="global-user-edit--password-sent-dialog">
        <DialogContent>
          <Typography>
            E-mail with link to change your password was sent to{" "}
            {userData?.Email}. Please follow the prompts
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPasswordSentDialogOpen(false)}
            variant="contained"
            data-testid="global-user-edit--password-sent-ok">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GlobalUserEdit;
